import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import DatePickerWithRange from "../../components/misc/date-picker-with-range";
import { AppSearchSectionSelect } from "../../components/appComponents";
import { Select } from "antd"; // Added for multi-select
import common from "../../helper/common";
import { DateminFormat, respEnum, BookingSummaryTypeEnum } from "../../constants/appConstant";

// Dashboard Components
import WelcomeBlock from "./components/welcome-block";
import ReportsArea from "./components/reports-area";
import RecentBookings from "./components/recent-bookings";
import IncomeSummary from "./components/income-summary";
import CourtWiseRevenue from "./components/court-wise-revenue";
import LineColumn from "./components/line-column";
import BookingSummary from "./components/booking-summary";
import TopCustomers from "./components/top-customers";
import PaidUnpaid from "./components/paid_unpaid";
import ProjectBudget from "./components/project-budget";
import SportWiseBookingsRevenue from "./components/sport-wise-bookings-revenue";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    welcomeBlock: {},
    usersBookingSummary: {},
    reportsArea: {},
    totalIncomeSummary: {}, 
    topCustomers: {},
    paidUnpaid: {},
    projectBudget: {},
    sportCourtRevenue: {}
  });
  const [courtId, setCourtId] = useState(null);
  const [courtList, setCourtList] = useState([]);
  const [selectedCourts, setSelectedCourts] = useState([]); // For CourtWiseRevenue multi-select
  const [sportCourtList, setSportCourtList] = useState([]); // Courts from sportCourtRevenue data
  const [selectedSport, setSelectedSport] = useState(null); // For sport selection
  const [isLoadingCourts, setIsLoadingCourts] = useState(false); // Loading state for court chart
  const [chartKey, setChartKey] = useState(0); // Key to force chart refresh
  const [isLoadingRecentBookings, setIsLoadingRecentBookings] = useState(false); // Loading state for recent bookings
  
  const firstDayOfMonth = common.moment.utc().startOf("month")._d;
  const lastDayOfMonth = common.moment.utc().endOf("month").startOf("day")._d;
  const [From, setFrom] = useState(firstDayOfMonth);
  const [To, setTo] = useState(lastDayOfMonth);

  useEffect(() => {
    fetchCourtList();
  }, []);

  useEffect(() => {
    if (From && To) {
      const formatedFrom = common.moment(From).format(DateminFormat);
      const formatedTo = common.moment(To).format(DateminFormat);
      getDashboardData(formatedFrom, formatedTo);
    } else {
      getDashboardData();
    }
  }, [From, To]);

  const fetchCourtList = async () => {
    try {
      const response = await common.ApiService.getInstance().request(
        "GetSportWiseCourtddl",
        {},
        "Get",
        false
      );
      if (response.status === respEnum.Success) {
        setCourtList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching court list:", error);
    }
  };

  // Extract courts from courtList data (grouped by court name)
  const extractCourtsFromCourtList = (courtListData) => {
    if (!Array.isArray(courtListData) || courtListData.length === 0) return [];
    
    const courtMap = new Map();
    
    // Group courts by name and collect all sports for each court
    courtListData.forEach(sport => {
      if (sport.options && Array.isArray(sport.options)) {
        sport.options.forEach(court => {
          const courtName = court.label;
          if (!courtMap.has(courtName)) {
            courtMap.set(courtName, {
              value: courtName, // Use court name as value
              label: courtName, // Will be updated with sports
              sports: []
            });
          }
          courtMap.get(courtName).sports.push(sport.label);
        });
      }
    });
    
    // Convert map to array and format labels
    const courts = Array.from(courtMap.values()).map(court => ({
      value: court.value,
      label: `${court.value} (${court.sports.join(', ')})`, // "Court 1 (Padel, Futsal, Tennis)"
      sports: court.sports
    }));
    
    // Sort by court name
    return courts.sort((a, b) => a.value.localeCompare(b.value));
  };

  // Extract sports from sportCourtRevenue data
  const extractSportsFromRevenueData = (sportCourtRevenue) => {
    if (!sportCourtRevenue) return [];
    
    try {
      let parsedData;
      if (typeof sportCourtRevenue === 'string') {
        parsedData = JSON.parse(sportCourtRevenue);
      } else {
        parsedData = sportCourtRevenue;
      }
      
      if (!Array.isArray(parsedData)) return [];
      
      return parsedData.map(sport => ({
        value: sport.sport,
        label: sport.sport
      }));
    } catch (error) {
      return [];
    }
  };

  // Set default sport to Padel if it exists in the data
  useEffect(() => {
    if (dashboardData.sportCourtRevenue && !selectedSport) {
      const sports = extractSportsFromRevenueData(dashboardData.sportCourtRevenue);
      const padelSport = sports.find(sport => sport.value.toLowerCase() === 'padel');
      if (padelSport) {
        setSelectedSport(padelSport.value);
      } else if (sports.length > 0) {
        setSelectedSport(sports[0].value);
      }
    }
  }, [dashboardData.sportCourtRevenue, selectedSport]);

  const fetchAllData = async () => {
    await getDashboardData();
  };

  // Function to fetch only type 2 data (Recent Bookings)
  const fetchRecentBookingsData = async () => {
    setIsLoadingRecentBookings(true);
    
    try {
      const query = new URLSearchParams();
      query.append("types", 2); // Only fetch type 2 data

      const response = await common.ApiService.getInstance().request(
        `GetDasboardData?${query.toString()}`,
        null,
        "Get",
        false, // showLoader = false to prevent global page loader
        false  // showMessage = false to prevent global messages
      );

      if (response.status == respEnum.Success) {
        const data = response?.data?.[0] || {};
        
        // Update only the usersBookingSummary part of dashboardData
        setDashboardData(prevData => ({
          ...prevData,
          usersBookingSummary: data.usersBookingSummary ?? 0
        }));
      }
    } catch (error) {
      console.error("Error fetching recent bookings data:", error);
    } finally {
      setIsLoadingRecentBookings(false);
    }
  };

  const getDashboardData = async (from, to) => {
    // Define all required types for dashboard components
    const types = [
      BookingSummaryTypeEnum.Welcome_Block,
      BookingSummaryTypeEnum.Users_Booking_Summary,
      BookingSummaryTypeEnum.Booking_Summary,
      BookingSummaryTypeEnum.Reports_Area,
      BookingSummaryTypeEnum.Total_Income_Summary,
      BookingSummaryTypeEnum.Top_Customers,
      BookingSummaryTypeEnum.Paid_Unpaid,
      BookingSummaryTypeEnum.Sport_Court_Revenue
    ];

    const query = new URLSearchParams();

    if (from) query.append("startDateTime", from);
    if (to) query.append("tillDate", to);
    //types.forEach(t => query.append("types", t));
    query.append("types", 1);
    query.append("types", 3);
    query.append("types", 4);
    query.append("types", 2);
    query.append("types", 5);
    query.append("types", 6);
    query.append("types", 9);
    query.append("types", 10);


    let response = await common.ApiService.getInstance().request(
      `GetDasboardData?${query.toString()}`
    );

    if (response.status == respEnum.Success) {
      const data = response?.data?.[0] || {};    
      const transformedData = {
        welcomeBlock: {
          todaysBookings: data.todaysBookings ?? 0,
          upcomingBookings: data.upcomingBookings ?? 0
        },
        reportsArea: {
          appBookings: data.appBookings ?? 0,
          clubBookings: data.clubBookings ?? 0,
          webBookings: data.webBookings ?? 0,
          whatsappBookings: data.whatsappBookings ?? 0,
          myAppBookings: data.myAppBookings ?? 0,
          appEarnings: data.appEarnings ?? 0,
          clubEarnings: data.clubEarnings ?? 0,
          webEarnings: data.webEarnings ?? 0,
          whatsappEarnings: data.whatsappEarnings ?? 0,
          myAppEarnings: data.myAppEarnings ?? 0,
          earnings: data.earnings ?? 0,
          appBookingHours: data.appBookingHours ?? 0,
          clubBookingHours: data.clubBookingHours ?? 0,
          webBookingHours: data.webBookingHours ?? 0,
          whatsappBookingHours: data.whatsappBookingHours ?? 0,
          myAppBookingHours: data.myAppBookingHours ?? 0,
          totalBookingHours: data.totalBookingHours ?? 0,
          totalBookings: data.totalBookings ?? 0,
          totalBookingHours: data.totalBookingHours ?? 0,
          cancelledBookings: data.cancelledBookings ?? 0,
          cancelledBookingHours: data.cancelledBookingHours ?? 0,
          netBookings: data.netBookings ?? 0,
          netBookingHours: data.netBookingHours ?? 0
        },
        paidUnpaid: {
          appEarnings: data.appEarnings ?? 0,
          clubEarnings: data.clubEarnings ?? 0,
          webEarnings: data.webEarnings ?? 0,
          whatsappEarnings: data.whatsappEarnings ?? 0,
          myAppEarnings: data.myAppEarnings ?? 0,
          earnings: data.earnings ?? 0,
          totalReceivedAmount: data.totalReceivedAmount ?? 0,
          totalUnpaidAmount: data.totalUnpaidAmount ?? 0
        },
        projectBudget: {
          appEarnings: data.appEarnings ?? 0,
          clubEarnings: data.clubEarnings ?? 0,
          webEarnings: data.webEarnings ?? 0,
          whatsappEarnings: data.whatsappEarnings ?? 0,
          myAppEarnings: data.myAppEarnings ?? 0,
          earnings: data.earnings ?? 0,
        },
        totalIncomeSummary: data.totalIncomeSummaryData ?? null,
        bookingSummary: data.bookingSummaryData ?? null,
        topCustomers: data.topCustomers ?? [],
        usersBookingSummary: data.usersBookingSummary ?? 0,
        sportCourtRevenue: data.sportCourtRevenue ?? null,
      };
      setDashboardData(transformedData);
    } else {
      setDashboardData({
        welcomeBlock: {},
        usersBookingSummary: {},
        reportsArea: {},
        totalIncomeSummary: {},
        topCustomers: {},
        paidUnpaid: {},
        projectBudget: {},
        sportCourtRevenue: {}
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="flex items-center flex-wrap justify-between gap-4">
        <h1 className="text-2xl font-medium text-default-800">Dashboard</h1>
        <DatePickerWithRange
          setFromTo={{ from: From, to: To }}
          onDateChanged={(val) => {
            const updatedFrom = val?.from
                  ? common.moment(val.from).format(DateminFormat)
              : undefined;
            const updatedTo = val?.to
              ? common.moment(val.to).format(DateminFormat)
              : undefined;

            if (updatedFrom !== From || updatedTo !== To) {
              setFrom(updatedFrom);
              setTo(updatedTo);
              getDashboardData(updatedFrom, updatedTo);
            }
          }}
        />
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Welcome & Charts */}
        <div className="col-span-12 md:col-span-5">
          <WelcomeBlock bookingSummary={dashboardData.welcomeBlock} />
          
          <Card className="mt-6">
            <CardContent className="p-6">
              <BookingSummary bookingSummary={dashboardData.bookingSummary} />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - User Table */}
        <div className="col-span-12 lg:col-span-7">
          <Card className='rounded-lg'>
            {isLoadingRecentBookings ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <div className="text-lg font-medium">Loading Recent Bookings...</div>
                  <div className="text-sm text-gray-500">Please wait while we refresh the data</div>
                </div>
              </div>
            ) : (
              <RecentBookings 
                bookingSummary={dashboardData.usersBookingSummary} 
                onRefresh={fetchRecentBookingsData}
              />
            )}
          </Card>
        </div>

        {/* Reports Section */}
        <div className="col-span-12">
          <ReportsArea bookingSummary={dashboardData.reportsArea} />
        </div>

        {/* Charts Section - Left */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader>
              <CardTitle>Income Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <IncomeSummary bookingSummary={dashboardData.totalIncomeSummary} />
            </CardContent>
          </Card>

          {/* <Card className="mt-6">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle>Performance Metrics</CardTitle>
              <AppSearchSectionSelect
                value={courtId}
                onChange={setCourtId}
                placeholder={"All"}
                //label="Court"
                data={courtList}
                className={"mb-0 w-48"}
              />
            </CardHeader>
            <CardContent>
              <LineColumn />
            </CardContent>
          </Card>*/}

          <Card className="mt-6">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle>Court Profit Overview</CardTitle>
              <Select
                placeholder="Select Sport"
                value={selectedSport}
                onChange={(value) => {
                  setIsLoadingCourts(true);
                  setSelectedSport(value);
                  // Force chart refresh by changing key
                  setChartKey(prev => prev + 1);
                  // Simulate loading delay
                  setTimeout(() => {
                    setIsLoadingCourts(false);
                  }, 500);
                }}
                style={{ width: 200}}
              >
                {extractSportsFromRevenueData(dashboardData.sportCourtRevenue).map(sport => (
                  <Select.Option key={sport.value} value={sport.value}>
                    {sport.label}
                  </Select.Option>
                ))}
              </Select>
            </CardHeader>
            <CardContent>
              {isLoadingCourts ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <div className="text-lg font-medium">Loading Courts...</div>
                    <div className="text-sm text-gray-500">Please wait while we load court data</div>
                  </div>
                </div>
              ) : (
                <CourtWiseRevenue 
                  key={chartKey} // Force refresh when key changes
                  sportCourtRevenue={dashboardData.sportCourtRevenue} 
                  selectedSport={selectedSport}
                />
              )}
            </CardContent>
          </Card>


          <Card className="mt-3">
            <CardHeader>
              <CardTitle>Sport Performance Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <SportWiseBookingsRevenue 
              sportCourtRevenue={dashboardData.sportCourtRevenue}
              />
            </CardContent>
          </Card>
        </div>

        {/* Charts Section - Right */}
        <div className="col-span-12 lg:col-span-6">
          <TopCustomers topCustomers={dashboardData.topCustomers} />
          
          <div className="grid grid-cols-12 gap-6 mt-6">
            <div className="col-span-6">
              <PaidUnpaid bookingSummary={dashboardData.paidUnpaid} />
            </div>
            <div className="col-span-6">
              {/* <GradiantDonut data={dashboardData.gradientDonut} /> */}
              <ProjectBudget bookingSummary={dashboardData.projectBudget} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;